import React from "react";
import { Helmet } from "react-helmet";
import { Link, graphql, useStaticQuery } from "gatsby";
import { buildBreadcrumbTrail, categoryStrapiId } from "./trail";
import { toAbsoluteUrl } from "../../utils/absolute-url";
import { jsonLd } from "../../utils/json-ld";

const Breadcrumbs = ({ category, product, size = "lg", disableClick = true }) => {
  const data = useStaticQuery(query);
  const categoryId = categoryStrapiId(category);
  if (!categoryId) {
    return (<></>);
  }

  // Le fil affiché et le fil balisé sont construits à partir du même trajet :
  // ils ne peuvent donc pas diverger (exigence Google).
  const trail = buildBreadcrumbTrail(data.allStrapiCategory.nodes, categoryId);
  const linkClass = "uppercase text-xs text-gray-800 underline";

  const breadcrumbListItems = [
    {
      "@type": "ListItem",
      "position": 1,
      "item": { "@id": "https://latelierdutotebag.com", "name": "Atelier du Tote Bag" }
    },
    ...trail.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 2,
      "item": { "@id": toAbsoluteUrl(crumb.url), "name": crumb.name }
    }))
  ];

  if (product) {
    breadcrumbListItems.push({
      "@type": "ListItem",
      "position": breadcrumbListItems.length + 1,
      "item": {
        "@id": toAbsoluteUrl(`/${category.slug}/${product.slug}`),
        "name": product.title
      }
    });
  }

  const breadcrumbJsonLd = jsonLd({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbListItems
  });

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{breadcrumbJsonLd}</script>
      </Helmet>
      <nav className="flex flex-wrap items-center">
        <Link to="/" className={`${linkClass} text-${size}`}>
          Atelier du Tote Bag
        </Link>
        {trail.length > 0 && (
          <span className="mx-2 text-xs text-gray-800">/</span>
        )}
        {trail.map((crumb, index) => {
          // Les ancêtres restent cliquables (ce sont des raccourcis de
          // navigation) ; seule la catégorie courante suit `disableClick`.
          // Seule la catégorie courante porte `text-${size}` : c'est déjà le
          // rendu actuel (les ancêtres recevaient un `text-undefined` inerte).
          const isCurrent = index === trail.length - 1;
          const className = isCurrent ? `${linkClass} text-${size}` : linkClass;
          return isCurrent && disableClick === true ? (
            <span key={crumb.strapiId} className={className}>
              {crumb.name}&nbsp;/&nbsp;
            </span>
          ) : (
            <a key={crumb.strapiId} href={crumb.url} className={className}>
              {crumb.name}&nbsp;/&nbsp;
            </a>
          );
        })}
      </nav>
    </>
  );
};

const query = graphql`
  query {
    allStrapiCategory {
      nodes {
        slug
        id
        name
        strapiId
        strapiParent {
          id
        }
        strapiChildren{
          id
        }
        products {
          id
        }
      }
    }
  }
`;

export default Breadcrumbs;
